// The pieces every work order surface shares: the status chips and the built
// ring. The ring is these pages' one moment: it fills as drones are marked
// built while somebody is looking, and flares when the run reaches its count.

import React from 'react';
import ProgressRing from '../components/ui/ProgressRing';
import { CATEGORY } from '../components/ui/categoryColors';
import Tag, { type TagProps } from '../components/ui/Tag';
import {
  STATUS_LABELS,
  UNIT_STATUS_LABELS,
  type UnitStatus,
  type WorkOrderStatus,
} from './workOrdersApi';

/** The text tier sized for a table row, beside `BTN_ROW`. */
export const BTN_ROW_TEXT =
  'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1.5 text-xs font-medium text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 transition-colors duration-150 ease-emphasized motion-reduce:transition-none';

// On hold and flagged keep their own orange, apart from the amber of a waiting suggestion.
const ORANGE = CATEGORY.orange.ink.hex;

type Look = Pick<TagProps, 'tone' | 'hex'>;

const STATUS_LOOK: Record<WorkOrderStatus, Look> = {
  suggested: { tone: 'warn' },
  draft: { tone: 'neutral' },
  released: { tone: 'accent' },
  in_progress: { tone: 'info' },
  complete: { tone: 'good' },
  closed: { tone: 'neutral' },
  on_hold: { hex: ORANGE },
  cancelled: { tone: 'neutral' },
};

const UNIT_LOOK: Record<UnitStatus, Look> = {
  in_progress: { tone: 'info' },
  complete: { tone: 'good' },
  flagged: { hex: ORANGE },
};

export const StatusChip: React.FC<{ status: WorkOrderStatus; className?: string }> = ({
  status,
  className = '',
}) => (
  <Tag size="sm" className={className} {...STATUS_LOOK[status]}>
    {STATUS_LABELS[status]}
  </Tag>
);

export const UnitStatusChip: React.FC<{ status: UnitStatus }> = ({ status }) => (
  <Tag size="sm" {...UNIT_LOOK[status]}>
    {UNIT_STATUS_LABELS[status]}
  </Tag>
);

/** A quiet fact chip for the header row: quantity, planned date. */
export const MetaChip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Tag variant="code">{children}</Tag>
);

/** Built out of quantity, as the kit's ring: green when the run fills, flaring once if it fills in view. */
export const BuiltRing: React.FC<{ done: number; total: number; size?: number }> = ({
  done,
  total,
  size = 20,
}) => <ProgressRing value={done} max={total} size={size} success decorative />;

/** The ring with its reading: "12 of 20 built". */
export const BuiltCount: React.FC<{ done: number; total: number }> = ({ done, total }) => (
  <ProgressRing value={done} max={total} size={20} display="count" unit="built" success />
);
